(() => {
  'use strict';
  if (customElements.get('lm-watch-gallery')) return;

  class WatchGallery extends HTMLElement {
    connectedCallback() {
      if (this.ready || this.waiting) return;
      if (document.readyState === 'loading' || !this.querySelector('[data-watch-card]')) {
        this.waiting = new AbortController();
        const initialize = () => {
          if (document.readyState === 'loading' || !this.querySelector('[data-watch-card]')) return;
          this.stopWaiting();
          this.connectedCallback();
        };
        document.addEventListener('DOMContentLoaded', initialize, { once: true, signal: this.waiting.signal });
        this.childObserver = new MutationObserver(initialize);
        this.childObserver.observe(this, { childList: true, subtree: true });
        this.waitTimer = setTimeout(() => this.stopWaiting(), 10000);
        return;
      }
      this.ready = true;
      this.generation = (this.generation || 0) + 1;
      this.events = new AbortController();
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.pageActive = true;
      this.inView = false;
      this.hovered = false;
      this.focused = this.contains(document.activeElement);
      this.userPaused = Boolean(this.userPaused);
      this.toggle = this.querySelector('[data-watch-toggle]');
      const signal = this.events.signal;
      this.cards = [...this.querySelectorAll('[data-watch-card]')].map(element => {
        const stage = element.querySelector('[data-watch-stage]');
        const current = stage?.querySelector('[data-watch-current]');
        if (!stage || !current) return null;
        const seen = new Set();
        const frames = [...element.querySelectorAll('template[data-watch-frame]')].map(template => {
          try {
            const url = new URL(template.dataset.src || '', location.href);
            if (!template.dataset.src || !['https:', 'http:'].includes(url.protocol) || seen.has(url.href)) return null;
            seen.add(url.href);
            return { src: url.href, alt: template.dataset.alt || current.alt || '', id: template.dataset.imageId || '', product: template.dataset.productId || '' };
          } catch (_) { return null; }
        }).filter(Boolean);
        const index = frames.findIndex(frame => frame.src === current.src);
        const card = { element, stage, fallback: element.querySelector('[data-watch-fallback]'), frames, current, index, failed: new Set(), visible: false, good: Boolean(current.complete && current.naturalWidth), nativePending: !current.complete, initialCancelled: false, busy: false, epoch: 0, next: element.querySelector('[data-watch-next]'), position: element.querySelector('[data-watch-position]') };
        current.classList.add('lm-watch-gallery__image', 'is-visible');
        current.classList.remove('is-entering');
        const loaded = () => {
          if (card.current !== current || card.initialCancelled || !this.ready) return;
          card.nativePending = false;
          card.good = true;
          current.hidden = false;
          this.sync();
        };
        const failed = () => {
          if (card.current !== current || card.initialCancelled || !this.ready) return;
          card.nativePending = false;
          card.good = false;
          if (card.index >= 0) card.failed.add(card.index);
          current.hidden = true;
          this.sync();
        };
        current.addEventListener('load', loaded, { signal });
        current.addEventListener('error', failed, { signal });
        if (current.complete && !current.naturalWidth) { card.good = false; if (index >= 0) card.failed.add(index); current.hidden = true; }
        card.next?.addEventListener('click', () => {
          if (card.busy || this.nextIndex(card) < 0) return;
          this.userPaused = true;
          this.sync();
          this.advance(card, true);
        }, { signal });
        return card;
      }).filter(Boolean);
      this.toggle?.addEventListener('click', () => {
        if (this.motion.matches) return;
        this.userPaused = !this.userPaused;
        this.sync();
      }, { signal });
      this.addEventListener('pointerenter', event => { if (event.pointerType !== 'touch') { this.hovered = true; this.sync(); } }, { signal });
      this.addEventListener('pointerleave', () => { this.hovered = false; this.sync(); }, { signal });
      this.addEventListener('pointercancel', () => { this.hovered = false; this.sync(); }, { signal });
      this.addEventListener('focusin', () => { this.focused = true; this.sync(); }, { signal });
      this.addEventListener('focusout', event => { this.focused = event.relatedTarget instanceof Node && this.contains(event.relatedTarget); this.sync(); }, { signal });
      this.motion.addEventListener('change', () => this.sync(), { signal });
      document.addEventListener('visibilitychange', () => this.sync(), { signal });
      window.addEventListener('pagehide', () => { this.pageActive = false; this.sync(); }, { signal });
      window.addEventListener('pageshow', () => { this.pageActive = true; this.sync(); }, { signal });
      if (typeof IntersectionObserver === 'function') {
        this.visibilityObserver = new IntersectionObserver(entries => {
          for (const entry of entries) {
            const visible = entry.isIntersecting && entry.intersectionRatio > 0;
            if (entry.target === this) this.inView = visible;
            else { const card = this.cards.find(item => item.element === entry.target); if (card) card.visible = visible; }
          }
          this.sync();
        }, { threshold: [0, 0.001] });
        this.visibilityObserver.observe(this);
        this.cards.forEach(card => this.visibilityObserver.observe(card.element));
      } else {
        const update = () => {
          if (this.visibilityFrame) return;
          this.visibilityFrame = requestAnimationFrame(() => {
            this.visibilityFrame = null;
            const visible = element => { const rect = element.getBoundingClientRect(); return rect.width > 0 && rect.height > 0 && rect.bottom > 0 && rect.top < innerHeight && rect.right > 0 && rect.left < innerWidth; };
            this.inView = visible(this);
            this.cards.forEach(card => { card.visible = visible(card.element); });
            this.sync();
          });
        };
        window.addEventListener('scroll', update, { passive: true, signal });
        window.addEventListener('resize', update, { passive: true, signal });
        update();
      }
      this.sync();
    }
    stopWaiting() {
      clearTimeout(this.waitTimer);
      this.waiting?.abort();
      this.waiting = null;
      this.childObserver?.disconnect();
      this.childObserver = null;
    }
    disconnectedCallback() {
      this.stopWaiting();
      this.ready = false;
      this.generation = (this.generation || 0) + 1;
      this.events?.abort();
      this.visibilityObserver?.disconnect();
      cancelAnimationFrame(this.visibilityFrame);
      this.visibilityFrame = null;
      this.cards?.forEach(card => { clearTimeout(card.timer); card.timer = null; this.cancelLoad(card); this.finishFade(card); });
      this.dataset.rotationState = 'disconnected';
    }
    presented(card) { return this.ready && this.isConnected && this.pageActive && document.visibilityState === 'visible' && this.inView && card.visible; }
    automatic(card) { return this.presented(card) && !card.nativePending && !this.userPaused && !this.motion.matches && !this.hovered && !this.focused; }
    nextIndex(card) {
      for (let step = 1; step <= card.frames.length; step++) {
        const index = (card.index + step + card.frames.length) % card.frames.length;
        if (!card.failed.has(index) && (index !== card.index || !card.good)) return index;
      }
      return -1;
    }
    state() {
      if (this.motion.matches) return 'reduced';
      if (!this.pageActive || document.visibilityState !== 'visible') return 'hidden';
      if (!this.inView) return 'offscreen';
      if (this.userPaused) return 'paused';
      if (this.hovered || this.focused) return 'interacting';
      return this.cards.some(card => this.nextIndex(card) >= 0) ? 'running' : 'static';
    }
    sync() {
      if (!this.ready || !this.cards) return;
      const state = this.state();
      this.dataset.rotationState = state;
      if (this.toggle) {
        this.toggle.hidden = this.motion.matches || !this.cards.some(card => card.frames.length > 1);
        this.toggle.disabled = this.motion.matches;
        const paused = this.userPaused || this.motion.matches;
        this.toggle.setAttribute('aria-pressed', String(paused));
        this.toggle.textContent = paused ? this.dataset.playLabel || 'Play images' : this.dataset.pauseLabel || 'Pause images';
      }
      this.cards.forEach(card => {
        const canAuto = this.automatic(card);
        if (!canAuto) { clearTimeout(card.timer); card.timer = null; }
        if (!this.presented(card) || (!canAuto && card.pending && !card.pending.manual)) this.cancelLoad(card);
        if (!this.presented(card) || this.motion.matches) this.finishFade(card);
        const next = this.nextIndex(card);
        card.element.dataset.currentIndex = String(card.index);
        card.element.dataset.currentImage = card.good ? card.frames[card.index]?.id || '' : '';
        card.element.dataset.rotationState = !card.visible ? 'offscreen' : card.busy || card.nativePending ? 'loading' : next < 0 ? card.good ? 'exhausted' : 'empty' : state;
        if (card.fallback) card.fallback.hidden = card.good;
        if (card.position) card.position.textContent = `${card.good && card.index >= 0 ? card.index + 1 : 0} / ${card.frames.length}`;
        if (card.next) { card.next.hidden = card.frames.length < 2; card.next.disabled = false; card.next.setAttribute('aria-disabled', String(card.busy || next < 0)); }
        if (canAuto && !card.busy && !card.timer && next >= 0) card.timer = setTimeout(() => { card.timer = null; this.advance(card, false); }, 3000);
      });
    }
    cancelLoad(card) {
      if (!card.pending && !card.busy) return;
      card.epoch++;
      card.pending?.cancel();
      card.pending = null;
      card.busy = false;
    }
    loadFrame(card, frame, manual) {
      return new Promise(resolve => {
        const image = new Image();
        let finished = false;
        let timeout;
        const finish = (status) => {
          if (finished) return;
          finished = true;
          clearTimeout(timeout);
          image.onload = null;
          image.onerror = null;
          if (status !== 'loaded') image.removeAttribute('src');
          resolve({ status, image });
        };
        card.pending = { manual, cancel: () => finish('cancelled') };
        image.className = 'lm-watch-gallery__image';
        image.alt = frame.alt;
        image.width = 480;
        image.height = 480;
        image.decoding = 'async';
        image.onload = async () => {
          try { if (typeof image.decode === 'function') await image.decode(); if (image.naturalWidth) finish('loaded'); else finish('failed'); }
          catch (_) { finish('failed'); }
        };
        image.onerror = () => finish('failed');
        timeout = setTimeout(() => finish('failed'), 12000);
        image.src = frame.src;
      });
    }
    async advance(card, manual) {
      if (card.busy || !this.presented(card) || (!manual && !this.automatic(card))) return;
      if (card.nativePending) {
        if (!manual) return;
        card.initialCancelled = true;
        card.nativePending = false;
        card.current.removeAttribute('srcset');
        card.current.removeAttribute('src');
        card.current.hidden = true;
      }
      const generation = this.generation;
      const epoch = ++card.epoch;
      clearTimeout(card.timer);
      card.timer = null;
      card.busy = true;
      this.sync();
      try {
        for (let attempts = 0; attempts < card.frames.length; attempts++) {
          const index = this.nextIndex(card);
          if (index < 0) break;
          const result = await this.loadFrame(card, card.frames[index], manual);
          if (generation !== this.generation || epoch !== card.epoch || !this.presented(card) || (!manual && !this.automatic(card)) || result.status === 'cancelled') break;
          card.pending = null;
          if (result.status === 'failed') { card.failed.add(index); continue; }
          this.commit(card, result.image, index);
          break;
        }
      } finally {
        if (generation === this.generation && epoch === card.epoch) { card.pending = null; card.busy = false; this.sync(); }
      }
    }
    commit(card, image, index) {
      this.finishFade(card);
      const previous = card.current;
      const animate = card.good && !this.motion.matches;
      previous.removeAttribute('data-watch-current');
      previous.setAttribute('aria-hidden', 'true');
      image.setAttribute('data-watch-current', '');
      if (animate) image.classList.add('is-entering');
      else image.classList.add('is-visible');
      card.stage.append(image);
      card.current = image;
      card.index = index;
      card.good = true;
      if (animate) {
        image.getBoundingClientRect();
        image.classList.add('is-visible');
        previous.classList.add('is-entering');
        previous.classList.remove('is-visible');
        card.outgoing = previous;
        card.fade = setTimeout(() => this.finishFade(card), 350);
      } else previous.remove();
    }
    finishFade(card) {
      clearTimeout(card.fade);
      card.fade = null;
      card.outgoing?.remove();
      card.outgoing = null;
      card.current?.classList.remove('is-entering');
    }
  }
  customElements.define('lm-watch-gallery', WatchGallery);
})();
